// 观察者管理器：单例模式实现
class ObserverManager {
  constructor() {
    // 存储不同配置的观察者实例
    this.observerMap = new Map()
    // 存储元素与回调的映射关系
    this.elementCallbacks = new WeakMap()
  }

  /**
   * 获取或创建观察者实例
   * @param {Object} options - 观察者配置
   * @returns {IntersectionObserver} 观察者实例
   */
  getObserver(options = {}) {
    // 将配置转换为唯一键（按属性名排序确保一致性）
    const configKey = this.generateConfigKey(options)
    
    // 如果已有相同配置的观察者，直接返回
    if (this.observerMap.has(configKey)) {
      return this.observerMap.get(configKey)
    }
    
    // 创建新的观察者实例
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // 从弱映射中获取该元素的回调函数
        const callback = this.elementCallbacks.get(entry.target)
        if (callback && typeof callback === 'function') {
          callback(entry.isIntersecting, entry)
        }
      })
    }, {
      // 默认配置
      root: null,
      rootMargin: '900px', // 默认提前一屏加载
      threshold: 0,
      // 合并用户配置
      ...options
    })
    
    // 缓存观察者实例
    this.observerMap.set(configKey, observer)
    return observer
  }

  /**
   * 观察元素
   * @param {HTMLElement} element - 要观察的元素
   * @param {Function} callback - 交叉状态变化时的回调
   * @param {Object} options - 观察者配置
   */
  observe(element, callback, options = {}) {
    if (!(element instanceof HTMLElement)) {
      console.error('观察的对象必须是DOM元素')
      return
    }
    
    if (typeof callback !== 'function') {
      console.error('回调必须是函数')
      return
    }
    
    // 存储元素与回调的映射
    this.elementCallbacks.set(element, callback)
    
    // 获取或创建观察者并开始观察
    const observer = this.getObserver(options)
    observer.observe(element)
  }

  /**
   * 停止观察元素
   * @param {HTMLElement} element - 要停止观察的元素
   * @param {Object} options - 观察者配置（需与观察时一致）
   */
  unobserve(element, options = {}) {
    if (!(element instanceof HTMLElement)) return
    
    // 清除元素的回调映射
    this.elementCallbacks.delete(element)
    
    // 停止观察
    const configKey = this.generateConfigKey(options)
    const observer = this.observerMap.get(configKey)
    if (observer) {
      observer.unobserve(element)
    }
  }

  /**
   * 销毁指定配置的观察者
   * @param {Object} options - 观察者配置
   */
  destroyObserver(options = {}) {
    const configKey = this.generateConfigKey(options)
    const observer = this.observerMap.get(configKey)
    
    if (observer) {
      observer.disconnect()
      this.observerMap.delete(configKey)
    }
  }

  /**
   * 销毁所有观察者
   */
  destroyAllObservers() {
    this.observerMap.forEach(observer => {
      observer.disconnect()
    })
    this.observerMap.clear()
    this.elementCallbacks.clear()
  }

  /**
   * 将配置对象转换为唯一字符串键
   * @param {Object} options - 配置对象
   * @returns {String} 唯一键
   */
  generateConfigKey(options) {
    // 提取配置中的有效属性并排序
    const validKeys = ['root', 'rootMargin', 'threshold']
    const config = {}
    
    validKeys.forEach(key => {
      if (options.hasOwnProperty(key)) {
        config[key] = options[key]
      }
    })
    
    // 转换为字符串作为键
    return JSON.stringify(config, (key, value) => {
      // 处理root为DOM元素的情况
      if (key === 'root' && value instanceof HTMLElement) {
        return `root_${value.id || value.tagName}`
      }
      return value
    })
  }
}

// 导出单例实例
export default new ObserverManager()
    